import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Plus, GraduationCap } from 'lucide-react';
import { SearchInput } from '@/components/shared/search-input';
import { useInstructorCourses } from '@/api/course-builder';
import { useProfile } from '@/api/profile';
import { useDebounce } from '@/hooks/use-debounce';
import { CourseFormDialog } from '@/components/forms/course-form';
import { LoadingSpinner } from '@/components/shared/loading-spinner';
import { Pagination } from '@/components/shared/pagination';
import type { CourseStatus } from '@/types';
import { cn } from '@/lib/utils';

const STATUS_LABELS: Record<CourseStatus, string> = {
  draft: 'Borrador',
  published: 'Publicado',
  archived: 'Archivado',
};

const STATUS_COLORS: Record<CourseStatus, string> = {
  draft:
    'bg-amber-50 text-amber-700 ring-1 ring-amber-600/20 dark:bg-amber-900/30 dark:text-amber-400 dark:ring-amber-400/20',
  published:
    'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/20 dark:bg-emerald-900/30 dark:text-emerald-400 dark:ring-emerald-400/20',
  archived:
    'bg-gray-50 text-gray-700 ring-1 ring-gray-600/20 dark:bg-gray-900/30 dark:text-gray-400 dark:ring-gray-400/20',
};

const TABS: { value: CourseStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'Todas' },
  { value: 'draft', label: 'Borrador' },
  { value: 'published', label: 'Publicadas' },
  { value: 'archived', label: 'Archivadas' },
];

export function InstructorCoursesPage() {
  const navigate = useNavigate();
  const { data: profile } = useProfile();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [statusFilter, setStatusFilter] = useState<CourseStatus | 'all'>('all');
  const [showCreate, setShowCreate] = useState(false);

  const { data, isLoading } = useInstructorCourses({
    page,
    per_page: 12,
    search: debouncedSearch || undefined,
    status: statusFilter === 'all' ? undefined : statusFilter,
    created_by: profile?.id,
  });

  const courses = data?.data ?? [];
  const pagination = data?.meta?.pagination;

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Mis Capacitaciones</h2>
          <p className="text-sm text-muted-foreground">Crea y administra tus capacitaciones</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" /> Nueva capacitación
        </button>
      </div>

      {/* Search + Status tabs */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <SearchInput
          className="max-w-sm flex-1"
          value={search}
          onChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          placeholder="Buscar capacitación..."
        />
        <div className="flex gap-1 rounded-lg border border-border p-1">
          {TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => {
                setStatusFilter(tab.value);
                setPage(1);
              }}
              className={cn(
                'min-h-[44px] rounded-md px-3 py-1 text-xs font-medium transition-colors',
                statusFilter === tab.value
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <LoadingSpinner />
      ) : courses.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border py-16">
          <GraduationCap className="mb-3 h-12 w-12 text-muted-foreground/50" />
          <p className="text-sm font-medium text-foreground">No tienes capacitaciones</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Crea tu primera capacitación para comenzar
          </p>
          <button
            onClick={() => setShowCreate(true)}
            className="mt-4 inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
          >
            <Plus className="h-4 w-4" /> Nueva capacitación
          </button>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {courses.map((course) => (
            <button
              key={course.id}
              onClick={() => navigate(`/instructor/courses/${course.id}`)}
              className="rounded-xl border border-border bg-card p-4 text-left transition-colors hover:bg-accent/50"
            >
              <div className="mb-2 flex items-center justify-between">
                <span
                  className={cn(
                    'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium',
                    STATUS_COLORS[course.status],
                  )}
                >
                  {STATUS_LABELS[course.status]}
                </span>
                <span className="text-xs text-muted-foreground">
                  {course._count?.enrollments ?? 0} inscritos
                </span>
              </div>
              <h3 className="text-sm font-semibold text-card-foreground line-clamp-2">
                {course.title}
              </h3>
              {course.description && (
                <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
                  {course.description}
                </p>
              )}
              <div className="mt-3 flex items-center gap-3 text-[10px] text-muted-foreground">
                {course.category && <span>{course.category}</span>}
                <span>
                  {new Date(course.created_at).toLocaleDateString('es-CR', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  })}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.total_pages > 1 && (
        <div className="mt-6">
          <Pagination meta={pagination} onPageChange={setPage} />
        </div>
      )}

      {/* Create dialog */}
      {showCreate && <CourseFormDialog onClose={() => setShowCreate(false)} />}
    </div>
  );
}
